import { Component, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';
import {
  AddOrderParams,
  OrdersParams,
  ServiceStatus,
} from 'src/app/models/orders';
import { ReservationDetail } from 'src/app/models/reservations';
import { IState } from 'src/app/reducers';
import {
  addSpecialOrder,
  changeOrderTable,
  deleteOrders,
  expediteOrder,
  fetchOrders,
  updateOrderComment,
  updateOrderStatus,
} from 'src/app/reducers/orders/orders.actions';
import {
  selectFiredOrdersItems,
  selectIsTableDataLoadingFired,
  selectTotalFiredOrdersCount,
} from 'src/app/reducers/orders/orders.selectors';
import { setSectionFilter } from 'src/app/reducers/shared/shared.actions';
import { selectSectionFilter } from 'src/app/reducers/shared/shared.selectors';
import {
  selectColumns,
  selectCurrentService,
  selectIsRequiresTableSeating,
  selectSections,
  selectTablesToShow,
} from 'src/app/reducers/user/user.selectors';
import { ClockService } from 'src/app/services/clock/clock.service';
import { AsyncPipe } from '@angular/common';
import { FiredOrdersTableComponent } from '../fired-orders-table/fired-orders-table.component';

@Component({
  selector: 'resto-fired',
  templateUrl: './fired-orders.component.html',
  styleUrls: ['./fired-orders.component.scss'],
  imports: [FiredOrdersTableComponent, AsyncPipe],
})
export class FiredOrdersComponent implements OnInit {
  private clockService = inject(ClockService);
  private ngrxStore = inject<Store<IState>>(Store);

  availableSections$ = this.ngrxStore.select(selectSections);
  clock$!: Observable<Date>;
  firedOrders$ = this.ngrxStore.select(selectFiredOrdersItems);
  isRequireTableSeating$ = this.ngrxStore.select(selectIsRequiresTableSeating);
  isTableDataLoading$ = this.ngrxStore.select(selectIsTableDataLoadingFired);
  lastParams!: Partial<OrdersParams>;
  selectedColumns$ = this.ngrxStore.select(selectColumns);
  sectionFilter$ = this.ngrxStore.select(selectSectionFilter);
  tablesToShow$ = this.ngrxStore.select(selectTablesToShow);
  totalOrdersCount$ = this.ngrxStore.select(selectTotalFiredOrdersCount);

  ngOnInit(): void {
    this.clock$ = this.clockService.getClock();
    this.ngrxStore
      .select(selectCurrentService)
      .pipe(take(1))
      .subscribe((val) => {
        if (val !== undefined)
          this.ngrxStore.dispatch(
            fetchOrders({ params: { service_status: 3 } }),
          );
      });
  }

  retrieveOrders(params: Partial<OrdersParams>) {
    this.lastParams = params;
    this.ngrxStore.dispatch(
      fetchOrders({
        params: {
          ...params,
          service_status: 3,
        },
      }),
    );
  }

  expediteOrder(data: {
    ids: number[];
    selectedReason: string;
    value: boolean;
  }): void {
    this.ngrxStore.dispatch(expediteOrder(data));
  }

  doneOrder(data: { ids: number[] }): void {
    this.ngrxStore.dispatch(
      updateOrderStatus({
        ids: data.ids,
        previous_status: 3,
        new_status: 4,
      }),
    );
  }

  undoOrder(data: { ids: number[] }): void {
    this.ngrxStore.dispatch(
      updateOrderStatus({
        ids: data.ids,
        previous_status: 3,
        new_status: 2,
      }),
    );
  }

  changeOrderTable(details: { data: Partial<ReservationDetail> }): void {
    this.ngrxStore.dispatch(
      changeOrderTable({
        data: details.data,
        service_status: 3,
      }),
    );
  }

  deleteOrder({
    ids,
    count,
    section,
  }: {
    ids: number[];
    count: number;
    section: string;
  }): void {
    this.ngrxStore.dispatch(
      deleteOrders({ ids, service_status: 3, count, section }),
    );
  }

  addOrder(detail: {
    data: { service_status: ServiceStatus } & Partial<AddOrderParams>;
  }): void {
    this.ngrxStore.dispatch(addSpecialOrder({ data: detail.data }));
  }

  filterBySection(section: string | null): void {
    this.ngrxStore.dispatch(setSectionFilter({ section }));
    [2, 3, 4].forEach((status) => {
      this.ngrxStore.dispatch(
        fetchOrders({
          params: {
            service_status: status,
          },
        }),
      );
    });
  }

  updateComment(data: { id: number; comment: string }): void {
    this.ngrxStore.dispatch(updateOrderComment(data));
  }
}
